import styled from '@emotion/styled';
import { Checkbox } from '@kompare/kompare-ui-library';
import DOMPurify from 'dompurify';
import React from 'react';
import { mediaQuery } from 'src/common/services/mediaQuery.service';
import { Flex, InputLabel, InputNumber, InputText } from 'src/components';
import { colors } from 'src/theme';
import { YesNoToggle } from '../../other/yesNoToggle.other';
import { Text } from '../../typography';
import { FollowUpFields } from './followUpFields.part';
import type { AnyQuestion, GqlQuestion, QuestionnaireAnswers, TableQuestion } from './healthQuestionnaire.types';
import { isFollowUpVisible } from './healthQuestionnaire.types';
import { TableQuestionSection } from './tableQuestion.part';

const QUESTION_ID_PREFIX = 'hq-field-';

const DescriptionHtml = styled.div({
  fontSize: 12,
  lineHeight: "16px",
  color: colors.text1,
  fontWeight: 400,

  '& ul': {
    margin: 0,
    marginLeft: 24,
    paddingLeft: 0,
    listStyleType: 'disc',
  },
  '& li': {
    marginBottom: 4,
    '&::marker': { fontSize: 10 },
  },
});

const ExtraInfoCardBox = styled.div({
  backgroundColor: colors.primary4,
  borderLeft: `3px solid ${colors.primary}`,
  borderRadius: 12,
  padding: 16,
});

const CheckboxRow = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '8px 0',
});

const ErrorText = styled.span({
  fontSize: 13,
  color: colors.error1,
  fontWeight: 500,
});

const QuestionCard = styled.div<{ $level: number }>(({ $level }) => ({
  backgroundColor: $level % 2 === 0 ? colors.background6 : '#FFFFFF',
  borderRadius: 12,
  padding: 16,
}));

const TextArea = styled.textarea<{ hasError: boolean }>(({ hasError }) => ({
  boxSizing: 'border-box',
  display: 'block',
  width: '100%',
  minHeight: 72,
  padding: '9px 12px',
  fontSize: 18,
  lineHeight: '24px',
  fontWeight: 400,
  fontFamily: 'Red Hat Display',
  color: colors.text1,
  backgroundColor: colors.white,
  borderRadius: 8,
  border: `1px solid ${hasError ? colors.error1 : colors.border5}`,
  outline: 'none',
  resize: 'vertical',
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',

  '&[disabled]': {
    border: `1px solid ${colors.border6}`,
    color: colors.text4,
    pointerEvents: 'none',
  },

  '&:focus': {
    borderColor: colors.border4,
  },
}));

const TextAreaValidationError = styled.div({
  color: colors.error1,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.14,
  paddingTop: 4,
});

const YesNoContainerDesktop = styled(Flex)({
  display: 'none !important',
  [mediaQuery('tablet')]: {
    display: 'flex!important',
  },
});

const YesNoContainerMobile = styled(Flex)({
  display: 'flex !important',
  flexDirection: 'column',
  alignItems: 'start',
  [mediaQuery('tablet')]: {
    display: 'none !important',
  },
});


interface TextAreaFieldProps {
  id: string;
  label: string;
  value: string;
  error?: string;
  rows?: number;
  onChange: (value: string) => void;
}

const TextAreaField: React.FC<TextAreaFieldProps> = ({ id, label, value, error, rows = 2, onChange }) => (
  <div>
    {label ? <InputLabel htmlFor={id}>{label}</InputLabel> : null}
    <TextArea
      id={id}
      rows={rows}
      value={value}
      hasError={!!error}
      onChange={(e) => onChange(e.target.value)}
    />
    {error ? <TextAreaValidationError>{error}</TextAreaValidationError> : null}
  </div>
);

interface QuestionSectionProps {
  question: AnyQuestion;
  /** Full questionnaire tree — needed so follow-up visibility can count nested yes answers under a group. */
  allQuestions: AnyQuestion[];
  answers: QuestionnaireAnswers;
  onChange: (answers: QuestionnaireAnswers) => void;
  errors?: Set<string>;
  /** 0 = root, 1 = direct child of a group, 2 = grandchild, … */
  level?: number;
  /** Render answers as static text instead of inputs/textareas (used by the print page). */
  displayMode?: boolean;
}

const DisplayValue = styled.div({
  boxSizing: 'border-box',
  width: '100%',
  padding: '9px 12px',
  fontSize: 18,
  lineHeight: '24px',
  fontWeight: 400,
  fontFamily: 'Red Hat Display',
  color: colors.text1,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
});

const HtmlDescription: React.FC<{ html: string }> = ({ html }) => (
  <DescriptionHtml dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
);

const ExtraInfoCard: React.FC<{ html: string }> = ({ html }) => (
  <ExtraInfoCardBox>
    <HtmlDescription html={html} />
  </ExtraInfoCardBox>
);

export { QUESTION_ID_PREFIX };

export const QuestionSection: React.FC<QuestionSectionProps> = ({ question, allQuestions, answers, onChange, errors, level = 0, displayMode }) => {
  const { code, type, title, description } = question;
  const extraInfo = 'extraInfo' in question ? question.extraInfo : undefined;
  const followUp = 'followUp' in question ? question.followUp : undefined;
  const childQuestions = 'questions' in question ? (question as GqlQuestion).questions : undefined;
  const questionOptions = 'options' in question ? (question as GqlQuestion).options : undefined;
  const lowerType = type?.toLowerCase();
  const hasError = errors?.has(code);

  const renderCard = (): React.ReactNode => {
    // ── yes_no_question ──────────────────────────────────────────────────
    if (lowerType === 'yes_no_question' || lowerType === 'yes_no' || lowerType === 'boolean') {
      return (
        <QuestionCard id={`${QUESTION_ID_PREFIX}${code}`} $level={level}>
          <Flex vertical gap={16}>
            {title && <Text type="medium" bold>{title}</Text>}
            <YesNoContainerDesktop gap={8}>
              {description && <HtmlDescription html={description} />}
              <Flex vertical gap={8} align="center" justify="center">
                <YesNoToggle
                  level={level}
                  value={answers.questions[code] ?? null}
                  onChange={(val) => onChange({ ...answers, questions: { ...answers.questions, [code]: val } })}
                />
              </Flex>
            </YesNoContainerDesktop>
            <YesNoContainerMobile gap={8}>
              {description && <HtmlDescription html={description} />}
              <Flex vertical gap={8} align="center" justify="center">
                <YesNoToggle
                  level={level}
                  value={answers.questions[code] ?? null}
                  onChange={(val) => onChange({ ...answers, questions: { ...answers.questions, [code]: val } })}
                />
              </Flex>
            </YesNoContainerMobile>
            {hasError && <ErrorText>Ovo polje je obavezno</ErrorText>}
            {followUp && isFollowUpVisible(followUp, answers, allQuestions) && (
              <FollowUpFields followUp={followUp} answers={answers} onChange={onChange} displayMode={displayMode} />
            )}
          </Flex>
        </QuestionCard>
      );
    }

    // ── input_text / input_number / input_string (same label + field pattern as InputText / InputNumber elsewhere) ──
    if (lowerType === 'input_text' || lowerType === 'input_number' || lowerType === 'input_string') {
      const fieldId = `${QUESTION_ID_PREFIX}${code}`;
      const err = hasError ? 'Ovo polje je obavezno' : '';
      const value = answers.textInputs[code] ?? '';
      const setValue = (next: string) =>
        onChange({ ...answers, textInputs: { ...answers.textInputs, [code]: next } });

      const commonProps = {
        id: fieldId,
        label: title ?? '',
        value,
        error: err,
        onChange: setValue,
      };

      let field: React.ReactNode;
      if (displayMode) {
        field = (
          <div>
            {title ? <InputLabel htmlFor={fieldId}>{title}</InputLabel> : null}
            <DisplayValue id={fieldId}>{value || '—'}</DisplayValue>
          </div>
        );
      } else if (lowerType === 'input_number') {
        field = <InputNumber {...commonProps} style={{ backgroundColor: colors.white }} />;
      } else if (lowerType === 'input_string') {
        field = <TextAreaField {...commonProps} rows={2} />;
      } else {
        field = <InputText {...commonProps} style={{ backgroundColor: colors.white }} />;
      }

      return (
        <QuestionCard id={`${QUESTION_ID_PREFIX}${code}`} $level={level}>
          <Flex vertical gap={8}>
            {description && <HtmlDescription html={description} />}          {field}
            {followUp && isFollowUpVisible(followUp, answers, allQuestions) && (
              <FollowUpFields followUp={followUp} answers={answers} onChange={onChange} displayMode={displayMode} />
            )}
          </Flex>
        </QuestionCard>
      );
    }

    // ── table (matrix of checkboxes: rows × columns) ─────────────────────
    if (lowerType === 'table') {
      return (
        <QuestionCard id={`${QUESTION_ID_PREFIX}${code}`} $level={level}>
          <TableQuestionSection
            question={question as TableQuestion}
            answers={answers}
            onChange={onChange}
            hasError={Boolean(hasError)}
          />
        </QuestionCard>
      );
    }

    // ── checkbox (with options) ──────────────────────────────────────────
    if (lowerType === 'checkbox' || lowerType === 'checkboxes') {
      const opts = questionOptions ?? [];
      const selected = answers.checkboxes[code] ?? [];

      const handleToggle = (optValue: string) => {
        const next = selected.includes(optValue)
          ? selected.filter((v) => v !== optValue)
          : [...selected, optValue];
        onChange({ ...answers, checkboxes: { ...answers.checkboxes, [code]: next } });
      };

      return (
        <QuestionCard id={`${QUESTION_ID_PREFIX}${code}`} $level={level}>
          <Flex vertical gap={16}>
            {title && <Text type="medium" bold>{title}</Text>}
            {description && <HtmlDescription html={description} />}          <Flex vertical gap={4}>
              {opts.map((opt) => (
                <CheckboxRow key={opt.value}>
                  <Checkbox
                    checked={selected.includes(opt.value)}
                    onChange={() => handleToggle(opt.value)}
                    checkboxSize="small"
                    label={<Text type="base">{opt.label}</Text>}
                  />
                </CheckboxRow>
              ))}
            </Flex>
            {hasError && <ErrorText>Ovo polje je obavezno</ErrorText>}
            {followUp && isFollowUpVisible(followUp, answers, allQuestions) && (
              <FollowUpFields followUp={followUp} answers={answers} onChange={onChange} displayMode={displayMode} />
            )}
          </Flex>
        </QuestionCard>
      );
    }

    // ── group_of_questions ───────────────────────────────────────────────
    // Renders the group title, then each child on its own line.
    if (lowerType === 'group_of_questions') {
      const items = childQuestions ?? [];

      return (
        <QuestionCard id={`${QUESTION_ID_PREFIX}${code}`} $level={level}>
          <Flex vertical gap={24}>
            {title && <Text type="medium" bold>{title}</Text>}
            {description && <HtmlDescription html={description} />}          {items.map((child) => (
              <QuestionSection
                key={child.code}
                question={child as AnyQuestion}
                allQuestions={allQuestions}
                answers={answers}
                onChange={onChange}
                errors={errors}
                level={level + 1}
                displayMode={displayMode}
              />
            ))}
            {followUp && isFollowUpVisible(followUp, answers, allQuestions) && (
              <FollowUpFields followUp={followUp} answers={answers} onChange={onChange} displayMode={displayMode} />
            )}
            {hasError && <ErrorText>Ovo polje je obavezno</ErrorText>}
          </Flex>
        </QuestionCard>
      );
    }

    // ── checkbox_group / multi_select ────────────────────────────────────
    if (lowerType === 'checkbox_group' || lowerType === 'multi_select') {
      const selected = answers.checkboxes[code] ?? [];
      const items = childQuestions ?? [];

      const handleToggle = (itemCode: string) => {
        const next = selected.includes(itemCode)
          ? selected.filter((c) => c !== itemCode)
          : [...selected, itemCode];
        onChange({ ...answers, checkboxes: { ...answers.checkboxes, [code]: next } });
      };

      return (
        <QuestionCard id={`${QUESTION_ID_PREFIX}${code}`} $level={level}>
          <Flex vertical gap={16}>
            {title && <Text type="medium" bold>{title}</Text>}
            {description && <HtmlDescription html={description} />}          <Flex vertical gap={4}>
              {items.map((item) => {
                const childFollowUp = item.followUp;
                return (
                  <div key={item.code} id={`${QUESTION_ID_PREFIX}${item.code}`}>
                    <CheckboxRow>
                      <Checkbox
                        checked={selected.includes(item.code)}
                        onChange={() => handleToggle(item.code)}
                        checkboxSize="small"
                        label={<Text type="base">{item.title ?? item.code}</Text>}
                      />
                    </CheckboxRow>
                    {childFollowUp && isFollowUpVisible(childFollowUp, answers, allQuestions) && (
                      <FollowUpFields followUp={childFollowUp} answers={answers} onChange={onChange} displayMode={displayMode} />
                    )}
                    {errors?.has(item.code) && <ErrorText>Ovo polje je obavezno</ErrorText>}
                  </div>
                );
              })}
            </Flex>
            {hasError && <ErrorText>Ovo polje je obavezno</ErrorText>}
            {followUp && isFollowUpVisible(followUp, answers, allQuestions) && (
              <FollowUpFields followUp={followUp} answers={answers} onChange={onChange} displayMode={displayMode} />
            )}
          </Flex>
        </QuestionCard>
      );
    }

    // ── group / section ──────────────────────────────────────────────────
    if (lowerType === 'group' || lowerType === 'section') {
      return (
        <QuestionCard id={`${QUESTION_ID_PREFIX}${code}`} $level={level}>
          <Flex vertical gap={24}>
            {title && <Text type="medium" bold>{title}</Text>}
            {description && <HtmlDescription html={description} />}          {childQuestions?.map((child) => (
              <QuestionSection key={child.code} question={child} allQuestions={allQuestions} answers={answers} onChange={onChange} errors={errors} level={level + 1} displayMode={displayMode} />
            ))}
          </Flex>
        </QuestionCard>
      );
    }

    return null;
  };

  return (
    <>
      {renderCard()}
      {extraInfo && <ExtraInfoCard html={extraInfo} />}
    </>
  );
};
